'use client';

import { BUY_IN_WAREHOUSE_FACET_VALUE } from '../SearchResultsFacets/constants';

import { ReactNode, useEffect, useRef, useState } from 'react';

import { FilterGroup } from '@/components/FilterGroup';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    GRS_DELIVERY_LOCATION_LABEL_KEY,
    GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE,
    GRS_WAREHOUSE_LABEL_KEY,
} from '@/src/components/SearchResultsFacets/grs.constants';
import {
    FacetBucketNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import type { GRSFacetConfigProperties } from '@/types/contentStack';
import { PageType } from '@/types/pages/search';
import { Box } from '@mui/material';
import { Accordion } from '@nxt-costco-com/forge-components';
import { SpaceLg, SpaceMd, SpaceXs } from '@nxt-costco-com/forge-design-tokens';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';

export function ProgramEligibilityFilterGroupV2({
    facet,
    bopimFacet,
    bopiwFacet,
    buyInWarehouseFacet,
    facetConfig,
    isBopimEligible,
    isBopiwEligible,
    grsFacetConfig,
    WarehouseSelectorComponent,
    DeliveryLocationSelectorComponent,
    locale,
    pageType,
    searchService,
}: {
    facet: FacetNormalized;
    bopimFacet?: FacetNormalized;
    bopiwFacet?: FacetNormalized;
    buyInWarehouseFacet?: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    isBopimEligible: boolean | undefined;
    isBopiwEligible: boolean | undefined;
    grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    WarehouseSelectorComponent: ReactNode;
    DeliveryLocationSelectorComponent: ReactNode;
    locale: LocaleProps;
    pageType: PageType;
    searchService: SearchServiceProps;
}) {
    const [isDeliveryExpanded, setIsDeliveryExpanded] = useState(true);
    const [isWarehouseExpanded, setIsWarehouseExpanded] = useState(true);
    const accordionRef = useRef<HTMLDivElement | null>(null);
    const deliveryLocationLabel = grsFacetConfig
        ? getKeyValue(GRS_DELIVERY_LOCATION_LABEL_KEY, grsFacetConfig)
        : '';
    const warehouseLabel = grsFacetConfig
        ? getKeyValue(GRS_WAREHOUSE_LABEL_KEY, grsFacetConfig)
        : '';

    useEffect(() => {
        const accordionInnerContent = accordionRef.current?.querySelector(
            "div.MuiAccordion-region[role='region']",
        );

        if (accordionInnerContent) {
            accordionInnerContent.removeAttribute('role');
        }
    }, []);

    const filterBuckets = ({
        buckets,
        valuesToRemove,
    }: {
        buckets: FacetBucketNormalized[];
        valuesToRemove: string[];
    }): FacetBucketNormalized[] => {
        return buckets.filter(
            bucket =>
                !valuesToRemove.includes(String(bucket.val).toLowerCase()),
        );
    };

    const facetFiltered = {
        ...facet,
        buckets: filterBuckets({
            buckets: facet.buckets,
            valuesToRemove: [
                GRS_USE_WAREHOUSE_INVENTORY_BUCKET_VALUE,
                BUY_IN_WAREHOUSE_FACET_VALUE?.toLowerCase(),
            ],
        }),
    };

    return (
        <>
            <Accordion
                accordionSummarySx={{
                    '&.MuiButtonBase-root': {
                        '&.MuiAccordionSummary-root': {
                            padding: `${SpaceMd} 0 ${SpaceXs} 0`,
                        },
                    },
                }}
                title={deliveryLocationLabel}
                // @ts-expect-error Accordion doesn't export this type
                variant="primary"
                isExpanded={isDeliveryExpanded}
                onChange={() => setIsDeliveryExpanded(!isDeliveryExpanded)}
                uniqueId={facet.facetKey}
                ref={accordionRef}
            >
                <Box
                    sx={{
                        paddingBottom: SpaceLg,
                        '& button': {
                            fontSize: 14,
                            textDecoration: 'underline',
                        },
                    }}
                >
                    {DeliveryLocationSelectorComponent}
                </Box>
                <FilterGroup
                    facet={facetFiltered}
                    facetConfig={facetConfig}
                    locale={locale}
                    pageType={pageType}
                    searchService={searchService}
                />
                {isBopimEligible && bopimFacet ? (
                    <FilterGroup
                        facet={bopimFacet}
                        facetConfig={facetConfig}
                        locale={locale}
                        pageType={pageType}
                        searchService={searchService}
                    />
                ) : (
                    <></>
                )}
            </Accordion>
            {(buyInWarehouseFacet || (isBopiwEligible && bopiwFacet)) && (
                <Accordion
                    accordionSummarySx={{
                        '&.MuiButtonBase-root': {
                            '&.MuiAccordionSummary-root': {
                                padding: `${SpaceMd} 0 ${SpaceXs} 0`,
                            },
                        },
                    }}
                    title={warehouseLabel}
                    // @ts-expect-error Accordion doesn't export this type
                    variant="primary"
                    isExpanded={isWarehouseExpanded}
                    onChange={() =>
                        setIsWarehouseExpanded(!isWarehouseExpanded)
                    }
                    uniqueId={facet.facetKey}
                    ref={accordionRef}
                >
                    <Box
                        sx={{
                            paddingBottom: SpaceLg,
                            '& button': {
                                fontSize: 14,
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {WarehouseSelectorComponent}
                    </Box>
                    {buyInWarehouseFacet ? (
                        <FilterGroup
                            facet={buyInWarehouseFacet}
                            facetConfig={facetConfig}
                            locale={locale}
                            pageType={pageType}
                            searchService={searchService}
                        />
                    ) : (
                        <></>
                    )}
                    {isBopiwEligible && bopiwFacet ? (
                        <FilterGroup
                            facet={bopiwFacet}
                            facetConfig={facetConfig}
                            locale={locale}
                            pageType={pageType}
                            searchService={searchService}
                        />
                    ) : (
                        <></>
                    )}
                </Accordion>
            )}
        </>
    );
}
